import axios from "axios";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../lib/api-client";
import axiosInstance from "../../api/axios";

// Risk reports and the client book authenticate with the shared `apikey`
// header, not the admin token, so they get their own instance rather than
// src/api/axios.js. Same host though. Note forex-risk only answers on
// production: staging's MT5 mirror is a 503. Transfers moved to the admin
// routes (token + role check), see adminGet below.
//
// ponytail: the key ships in the browser bundle, same as the mining key. The
// handoff docs warn it reads every account; move behind a proxy if that matters.
const client = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: { apikey: process.env.REACT_APP_FOREX_REPORTS_API_KEY || "" },
  timeout: 30000,
});

// A 503 must read as an error, never as a quiet book, so every failure becomes
// an ApiError carrying the backend's own message for ErrorBanner.
client.interceptors.response.use(
  (r) => r,
  (error) =>
    Promise.reject(
      new ApiError(
        error.response?.status ?? 0,
        error.response?.data?.message || error.message || "Request failed",
        error.response?.data,
      ),
    ),
);

// A malformed filter is a 400 rather than ignored, so blanks are never sent.
const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== "" && v != null),
  );

// Rows sit at data.data, paging figures beside them.
const get = (path, params) =>
  client.get(path, { params: clean(params) }).then((r) => r.data.data);

// 60 requests a minute and the key does not exempt us: no polling, no refetch
// on focus. The Refresh button is the only way to ask again.
// Previous rows are kept while paging, but not across tabs: another report's
// rows would render under this report's columns.
const opts = (tab) => ({
  placeholderData: (prev, prevQuery) =>
    prevQuery?.queryKey[1] === tab ? prev : undefined,
  refetchOnWindowFocus: false,
  staleTime: 10_000,
});

export function useForexRisk(report, filters) {
  return useQuery({
    queryKey: ["forex-risk", report, filters],
    queryFn: () => get(`/api/forex-risk/${report}`, filters),
    ...opts(report),
  });
}

// Closed trades page by cursor, so "Load more" appends the next page rather than
// replacing it.
export function useClosedTradesFeed(filters) {
  return useInfiniteQuery({
    queryKey: ["forex-risk", "closed-trades", "feed", filters],
    queryFn: ({ pageParam }) =>
      get("/api/forex-risk/closed-trades", { ...filters, cursor: pageParam }),
    initialPageParam: "",
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
}

// The client book: a row per Ginox user with their accounts summed, and the
// same selection totalled. Header figures come from the summary, never from
// adding up a page.
export function useForexClients(filters) {
  return useQuery({
    queryKey: ["forex-risk", "clients", filters],
    queryFn: () => get("/api/forex-risk/clients", filters),
    ...opts("clients"),
  });
}

export function useForexClientsSummary(filters) {
  return useQuery({
    queryKey: ["forex-risk", "clients-summary", filters],
    queryFn: () => get("/api/forex-risk/clients/summary", filters),
    ...opts("clients-summary"),
  });
}

// Transfers read the admin routes: the panel's token (via the axios
// interceptor) and a role check, no apikey. Same body as the old routes.
const adminGet = (path, params) =>
  axiosInstance
    .get(path, { params: clean(params) })
    .then((r) => r.data.data)
    .catch((error) => {
      throw new ApiError(
        error.response?.status ?? 0,
        error.response?.data?.message || error.message || "Request failed",
        error.response?.data,
      );
    });

export function useTransfers(direction, filters) {
  return useQuery({
    queryKey: ["forex-transfers", direction, filters],
    queryFn: () => adminGet(`/api/admin/transfers/${direction}`, filters),
    ...opts(direction),
  });
}

// Every direction totalled in one call. Completed transfers only, by design:
// the route has no status param and ignores one if sent.
export function useTransferSummary(filters) {
  return useQuery({
    queryKey: ["forex-transfers", "summary", filters],
    queryFn: () => adminGet("/api/admin/transfers/summary", filters),
    ...opts("summary"),
  });
}

export const RISK_REPORTS = [
  { value: "profit", label: "Profit" },
  { value: "closed-trades", label: "Closed Trades" },
  { value: "open-positions", label: "Open Positions" },
  { value: "pending-orders", label: "Pending Orders" },
];

export const TRANSFER_DIRECTIONS = [
  { value: "forex-to-mt5", label: "Forex → MT5", mt5: true, summaryKey: "forexToMt5" },
  { value: "mt5-to-forex", label: "MT5 → Forex", mt5: true, summaryKey: "mt5ToForex" },
  { value: "forex-to-gusd", label: "Forex → GUSD", summaryKey: "forexToGusd" },
  { value: "gusd-to-forex", label: "GUSD → Forex", summaryKey: "gusdToForex" },
];

export const TRANSFER_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "reversed", label: "Reversed" },
  { value: "stranded", label: "Stranded" },
];

export const PAGE_SIZES = [20, 50, 100, 200];
